/* eslint-disable @typescript-eslint/no-explicit-any */
import "./FlyoutPanel.css";

import { ReactNode, RefObject, UIEvent } from "react";

import { ClosePanelArrowRightBlueIcon } from "../../../assets";
import { Button } from "../../forms";
import { Backdrop } from "../Backdrop";
import { useFlyoutPanel } from "./useFlyoutPanel";

interface FlyoutPanelProps {
  /**
   * @param show should be part of a state variable (eg. [show, setShow]) set in the parent page class
   */
  show: boolean;
  onClose: () => void;
  size: "ExtraSmall" | "Small" | "Medium" | "Large";
  height?: "Default" | "Full";
  header: string;
  buttonsChildren: ReactNode;
  contentChildren: ReactNode;
  className?: string;
  onScrollContent?: (e: UIEvent<HTMLElement>) => void;
  contentAreaRef?: RefObject<HTMLDivElement>;
  /** When false the header scrolls away with the content instead of staying pinned above it. */
  stickyHeader?: boolean;
}

export const FlyoutPanel = ({
  show,
  onClose,
  size,
  height = "Default",
  header,
  buttonsChildren,
  contentChildren,
  className,
  onScrollContent,
  contentAreaRef,
  stickyHeader = true,
}: FlyoutPanelProps): JSX.Element | null => {
  const { flyoutPanelRefCallBack, onFlyoutPanelKeyDownHandle, showTransition } = useFlyoutPanel({
    show,
    onClose,
  });

  const headerArea = (
    <div className="HeaderArea">
      {size === "ExtraSmall" ? <h2>{header}</h2> : <h1>{header}</h1>}
      <div className="ButtonsArea">{buttonsChildren}</div>
    </div>
  );

  return show ? (
    <>
      <div
        ref={flyoutPanelRefCallBack}
        className={`FlyoutPanel ${size} ${showTransition ? "Open" : ""} ${
          stickyHeader ? "" : "FlyoutPanel_ScrollHeader"
        } ${className ?? ""}`}
        role="button"
        onKeyDown={onFlyoutPanelKeyDownHandle}
        tabIndex={0}
      >
        <div className="ClosePanelButtonWrapper">
          <Button text="Close panel" icon={<ClosePanelArrowRightBlueIcon />} variant="secondary" onClick={onClose} />
        </div>
        {stickyHeader && headerArea}
        <div className={`ContentArea ${height}`} ref={contentAreaRef} onScroll={onScrollContent}>
          {!stickyHeader && headerArea}
          {contentChildren}
        </div>
      </div>
      <Backdrop show={show} />
    </>
  ) : null;
};
